package org.example.project.setup

import kotlinx.browser.window
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.decodeFromJsonElement
import kotlinx.serialization.json.put
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.utils.All
import kotlinx.coroutines.await
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

object SetupApi {
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    suspend fun customerNodeExists(firmenId: String): Boolean =
        decodeBool(LumaticaApiManager.call("setup/customer-node-exists/${apiPart(firmenId)}"), "exists")

    suspend fun ensureCustomerNodeSetup(firmenId: String, lizenzkey: String, nodePort: String): Boolean =
        decodeBool(
            LumaticaApiManager.call(
                "setup/customer-node-setup/${apiPart(firmenId)}/${apiPart(lizenzkey)}/${apiPart(nodePort)}"
            ),
            "ok"
        )

    suspend fun loadCusNodeSettings(): All? =
        runCatching {
            val response = window.fetch("/node/status").await()
            if (!response.ok) return@runCatching null
            val settings = response.json().await()
            All(
                id = "setupsettings",
                firmenId = (settings.asDynamic().firmenId as? String).orEmpty(),
                nodeEingerichtet = (settings.asDynamic().nodeEingerichtet as? Boolean) == true
            )
        }.getOrNull()

    suspend fun saveCusNodeEingerichtet(nodeEingerichtet: Boolean): All? =
        decodeAll(
            LumaticaApiManager.call(
                "setup/node-settings-save/${apiPart(nodeEingerichtet.toString())}"
            )
        )

    suspend fun loadCusDeckblatt(firmenId: String): All? =
        decodeAll(LumaticaApiManager.call("setup/customer-deckblatt/${apiPart(firmenId)}"))

    suspend fun saveCusDeckblatt(firmenId: String, state: All): All? =
        decodeAll(
            LumaticaApiManager.call(
                listOf(
                    "setup",
                    "customer-deckblatt-save",
                    firmenId,
                    state.firma,
                    state.geschaeftsfuehrer,
                    state.webseite,
                    state.gegruendetAm
                ).joinToString("/") { apiPart(it) }
            )
        )

    suspend fun loadCusCompany(): All? =
        decodeAll(LumaticaApiManager.call("setup/cus-company"))

    suspend fun saveCusCompany(company: All): All? =
        postSetupAll("setup/cus-company-json", company)

    suspend fun loadCusUsers(): List<All> =
        decodeAllList(LumaticaApiManager.call("setup/cus-users"))

    suspend fun loadCusUser(userId: String): All? =
        decodeAll(LumaticaApiManager.call("setup/cus-user/${apiPart(userId)}"))

    suspend fun saveCusUser(user: All): All? =
        postSetupAll(
            "setup/cus-user-json",
            buildJsonObject {
                put("preferredId", user.id)
                put("user", json.encodeToJsonElement(All.AllNames.serializer(), user))
            }
        )

    suspend fun changeCusUserPassword(userId: String, password: String): All? =
        postSetupAll(
            "setup/cus-user-password-json",
            buildJsonObject {
                put("userId", userId)
                put("password", password)
            }
        )

    suspend fun saveCusRole(userId: String, firmenId: String, role: All): All? =
        postSetupAll(
            "setup/cus-role-json",
            buildJsonObject {
                put("userId", userId)
                put("firmenId", firmenId)
                put("role", json.encodeToJsonElement(All.AllNames.serializer(), role))
            }
        )

    suspend fun loadCusStandorte(): List<All> =
        decodeAllList(LumaticaApiManager.call("setup/cus-standorte"))

    suspend fun saveCusStandort(firmenId: String, standort: All): All? =
        postSetupAll(
            "setup/cus-standort-json",
            buildJsonObject {
                put("firmenId", firmenId)
                put("standort", json.encodeToJsonElement(All.AllNames.serializer(), standort))
            }
        )

    suspend fun loadSysSetupSettingsExists(): Boolean {
        val raw = LumaticaApiManager.call("setup/sys-settings")
        return raw.isNotBlank() && raw != "null"
    }

    suspend fun loadSysBootstrapState(): All? =
        decodeAll(LumaticaApiManager.call("setup/sys-bootstrap-state"))

    suspend fun completeSysBootstrap(unternehmen: All, user: All, role: All, password: String): All? {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/setup/sys-bootstrap-json",
            RequestInit(
                method = "POST",
                headers = Headers().apply { append("Content-Type", "application/json") },
                body = json.encodeToString(
                    buildJsonObject {
                        put("unternehmen", json.encodeToJsonElement(All.AllNames.serializer(), unternehmen))
                        put("user", json.encodeToJsonElement(All.AllNames.serializer(), user))
                        put("role", json.encodeToJsonElement(All.AllNames.serializer(), role))
                        put("password", password)
                    }
                )
            )
        ).await()
        return decodeAll(response.text().await())
    }

    suspend fun saveSysSetupSettings(data: All): All? =
        decodeAll(
            LumaticaApiManager.call(
                "setup/sys-settings-save/${apiPart(data.id)}/${apiPart(data.nodeEingerichtet.toString())}"
            )
        )

    private fun decodeBool(raw: String, key: String): Boolean =
        runCatching { json.parseToJsonElement(raw).jsonObject[key]?.jsonPrimitive?.content == "true" }
            .getOrDefault(false)

    private fun decodeAll(raw: String): All? {
        if (raw.isBlank() || raw == "null") return null
        return runCatching {
            json.decodeFromJsonElement(All.AllNames.serializer(), json.parseToJsonElement(raw))
        }.getOrNull()
    }

    private fun decodeAllList(raw: String): List<All> {
        if (raw.isBlank() || raw == "null") return emptyList()
        return runCatching { json.decodeFromString(ListSerializer(All.AllNames.serializer()), raw) }.getOrDefault(emptyList())
    }

    private suspend fun postSetupAll(endpoint: String, body: All): All? =
        postSetupAll(endpoint, json.encodeToJsonElement(All.AllNames.serializer(), body))

    private suspend fun postSetupAll(endpoint: String, body: kotlinx.serialization.json.JsonElement): All? {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$endpoint",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(body)
            )
        ).await()
        return decodeAll(response.text().await())
    }

    private suspend fun currentNodeFirmenId(): String? =
        runCatching {
            val settings = window.fetch("/node/status").then { it.json() }.await()
            (settings.asDynamic().firmenId as? String)?.takeIf { it.isNotBlank() }
        }.getOrNull()

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String
}
